import { ChatEntry, ResponseEntry, ResponseInfoType, ApiRequestParams } from './types';
import { API_BASE_URL } from './config/apiBase';
import {
  isSessionExpiredMessage,
  isSessionExpiredResponse,
  triggerSessionExpired,
} from './utils/sessionExpired';

let sessionId: string | null = null;
let sessionInitPromise: Promise<string> | null = null;

const fetchSessionFromServer = async (): Promise<string> => {
  const response = await fetch(`${API_BASE_URL}/session/init`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json; charset=utf-8',
    },
    body: JSON.stringify({}),
  });

  if (!response.ok) {
    throw new Error(`session init failed: ${response.status}`);
  }

  const data = (await response.json()) as { sessionID: string };
  return data.sessionID;
};

/** 서버 발급 세션 확보 (현재 탭·페이지 로드당 1회) */
export const ensureSessionId = async (): Promise<string> => {
  if (sessionId) {
    return sessionId;
  }

  if (!sessionInitPromise) {
    sessionInitPromise = (async () => {
      try {
        const id = await fetchSessionFromServer();
        sessionId = id;
        return id;
      } catch (err) {
        // 백엔드가 아직 안 떠 있으면 promise를 비워 다음 호출(질문 전송 등)에서 재시도
        sessionInitPromise = null;
        throw err;
      }
    })();
  }

  return sessionInitPromise;
};

/** 새로고침·홈(새 대화) — 서버에서 새 세션 발급 */
export const refreshSessionId = async (): Promise<string> => {
  sessionId = null;
  sessionInitPromise = null;
  const id = await fetchSessionFromServer();
  sessionId = id;
  sessionInitPromise = Promise.resolve(id);
  return id;
};

export const getSessionId = (): string => {
  if (!sessionId) {
    throw new Error('Session not initialized. Call ensureSessionId() first.');
  }
  return sessionId;
};

export const fetchSSE = async (
  params: { question: string; fromCategory?: boolean; language?: string }, //string,
  callback: (
    entries: ChatEntry[],
    infoData: ResponseInfoType,
    conversationId: string | undefined,
  ) => void,
  controller: AbortController,
  retries = 5,
) => {
  const { question, fromCategory, language } = params;
  const activeSessionId = await ensureSessionId();

  const handleSessionExpiredInStream = (messages: string[]): boolean => {
    const combined = messages
      .map((msg) => (typeof msg === 'string' ? msg : ''))
      .join('');
    if (!isSessionExpiredMessage(combined)) {
      return false;
    }
    triggerSessionExpired();
    controller.abort();
    return true;
  };

  const fetchWithTimeout = async (timeout: number) => {
    const timeoutPromise = new Promise((_, reject) =>
      setTimeout(() => reject(new Error('Request timed out')), timeout),
    );

    const fetchPromise = fetch(`${API_BASE_URL}/llm?stream=true`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json; charset=utf-8',
      },
      body: JSON.stringify({
        text: question,
        fromCategory: fromCategory,
        sessionID: activeSessionId,
        language: language,
      } as ApiRequestParams),
      signal: controller.signal,
    });

    return Promise.race([fetchPromise, timeoutPromise]) as Promise<Response>;
  };
  try {
    const response = await fetchWithTimeout(60000); // 3a초 타임아웃 설정
    if (await isSessionExpiredResponse(response)) {
      triggerSessionExpired();
      return;
    }
    if (response.ok) {
      const reader = response.body!.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let conversationId: string | undefined = undefined;

      const processChunk = async (): Promise<void> => {
        const { value, done } = await reader.read();
        if (done) {
          if (buffer) {
            const entries = jsonToData(buffer);
            conversationId =
              conversationId || entries[0]?.data['conversation-id']; // 처음 나타나는 conversation-id 추출
            const messages = entries
              .filter((entry) => entry.event !== 'result')
              .map((entry) => entry.data.message?.content)
              .filter(Boolean);
            if (handleSessionExpiredInStream(messages)) {
              return;
            }
            const infoData = formatEventData(buffer);
            callback(messages, infoData, conversationId);
          }
          return;
        }

        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split('\n\n');
        buffer = parts.pop() || '';

        for (const part of parts) {
          const entries = jsonToData(part);
          if (!conversationId) {
            conversationId = entries[0]?.data['conversation-id']; // 처음 나타나는 conversation-id 추출
          }
          const messages = entries
            .filter((entry) => entry.event !== 'result')
            .map((entry) => entry.data.message?.content)
            .filter(Boolean);
          if (handleSessionExpiredInStream(messages)) {
            return;
          }
          const infoData = formatEventData(part);
          callback(messages, infoData, conversationId);
        }

        await processChunk();
      };

      await processChunk();
    } else {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
  } catch (error) {
    if ((error as Error).name === 'AbortError') {

    } else if (retries > 0) {
      // console.error(
      //   `Fetch SSE Error: ${
      //     (error as Error).message
      //   }. Retrying... (${retries} retries left)`,
      // );
      await fetchSSE(params, callback, controller, retries - 1);
    } else {
      // console.error('Fetch SSE Error:', error);
      alert('에러입니다.');
    }
  }
};

const jsonToData = (responseData: any): ResponseEntry[] => {
  const entries = responseData.split('id:');

  const results: ResponseEntry[] = entries
    .map((entry: string, index: number) => {
      if (entry.trim() === '' && index === 0) return null; // 첫 번째 빈 항목 제외

      const eventDataIndex = entry.indexOf('event:');
      const tokenDataIndex = entry.indexOf('data:', eventDataIndex) + 5; // 'data:' 문자열 다음 인덱스

      // id가 없는 경우도 처리
      const idSegment = entry.substring(0, eventDataIndex).trim();
      const id = idSegment.startsWith('event:') ? undefined : idSegment;

      const eventData = entry
        .substring(eventDataIndex + 6, tokenDataIndex - 5)
        .trim();
      const jsonData = entry.substring(tokenDataIndex).trim(); // JSON 시작부터 문자열 끝까지

      try {
        // JSON 구조의 끝을 찾기 위해 첫 번째 괄호의 쌍을 찾는 방식 사용
        let braceCount = 0;
        let endIndex = 0;
        for (let i = 0; i < jsonData.length; i++) {
          if (jsonData[i] === '{') {
            braceCount++;
          } else if (jsonData[i] === '}') {
            braceCount--;
            if (braceCount === 0) {
              endIndex = i + 1;
              break;
            }
          }
        }

        const validJson = jsonData.substring(0, endIndex); // 유효한 JSON만 파싱
        const parsedData = validJson ? JSON.parse(validJson) : null; // null 처리
        const result: ResponseEntry = {
          id,
          data: parsedData ?? {},
        };
        if (eventData) {
          result.event = eventData;
        }
        // 여기서 conversation-id 추가
        if (parsedData && parsedData.conversation_id) {
          result.conversation_id = parsedData.conversation_id;
        }
        return result;
      } catch (error) {
        // console.error('JSON 파싱 에러:', error);
        return null;
      }
    })
    .filter(Boolean) as ResponseEntry[]; // null 제거 후 타입 어설션
  return results;
};

function formatEventData(response: any) {
  const events = response.split('event:');
  let infoData: ResponseInfoType = {
    url: [],
    contact: {
      name: '',
      email: '',
      department: '',
      phone: '',
    },
    images: [],
    rich_sources: [],
    orignlFileNm: undefined,
    fileUrl: undefined,
    fileExtsn: undefined,
  };

  events.forEach((event: any) => {
    const lines = event.split('\n').map((line: any) => line.trim());
    const dataLine = lines.find((line: any) => line.startsWith('data:'));
    if (dataLine) {
      const jsonString = dataLine.slice(5).trim();
      try {
        const dataObject = jsonString ? JSON.parse(jsonString) : {}; // null 처리
        if (dataObject && typeof dataObject === 'object') {
          // 백엔드에서 metadata 안에 rich_sources를 보내는 경우 처리
          const richSources = dataObject.metadata?.rich_sources || dataObject.rich_sources || [];
          const urlData = (dataObject.metadata?.url || dataObject.url || []).filter(
            (u: unknown) => typeof u === 'string' && u.trim() !== '' && !String(u).toLowerCase().includes('/roadkill'),
          );
          const filteredRichSources = Array.isArray(richSources)
            ? richSources.filter(
                (s: { url?: string }) => !(typeof s?.url === 'string' && s.url.toLowerCase().includes('/roadkill')),
              )
            : [];
          
          infoData = {
            url: urlData,
            permitEntryUrl: dataObject.metadata?.permitEntryUrl || dataObject.permitEntryUrl,
            contact: {
              name: (dataObject.metadata?.name || dataObject.contact?.name) ?? '',
              email: dataObject.contact?.email ?? '',
              department: (dataObject.metadata?.department || dataObject.contact?.department) ?? '',
              phone: (dataObject.metadata?.phone || dataObject.contact?.phone) ?? '',
            },
            images: dataObject.images ?? [],
            rich_sources: filteredRichSources.length > 0 ? filteredRichSources : undefined, // 빈 배열이면 undefined로
            library_after_books:
              dataObject.metadata?.library_after_books ||
              dataObject.library_after_books ||
              undefined,
            library_ui_mode:
              dataObject.metadata?.library_ui_mode ||
              dataObject.library_ui_mode ||
              undefined,
            ui: dataObject.metadata?.ui || dataObject.ui || undefined,
            // 파일 정보 추가
            orignlFileNm: dataObject.metadata?.orignlFileNm || dataObject.orignlFileNm,
            fileUrl: dataObject.metadata?.fileUrl || dataObject.fileUrl,
            fileExtsn: dataObject.metadata?.fileExtsn || dataObject.fileExtsn,
          };
        }
      } catch (error) {
        // console.error('Error parsing JSON data:', error);
      }
    }
  });

  return infoData; // 객체 반환
}


export const FeedbackApi = async (
  vote: number,
  conversationId: string | undefined,
) => {
  try {
    const response = await fetch(`${API_BASE_URL}/feedback/vote`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json; charset=utf-8',
      },
      body: JSON.stringify({
        conversation_id: conversationId,
        vote: vote,
      }),
    });

    if (response.ok) {
      // console.log('response Vote is success');
      //console.log(response)
    } else {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
  } catch (error) {
    // console.error('Fetch SSE Error:', error);
    alert('에러입니다.');
  }
};

export const FeedbackCommentApi = async (
  feedbackComment: string,
  conversationId: string | undefined,
) => {
  try {
    const response = await fetch(`${API_BASE_URL}/feedback/comment`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json; charset=utf-8',
      },
      body: JSON.stringify({
        conversation_id: conversationId,
        comment: feedbackComment,
      }),
    });

    if (response.ok) {
      // console.log('response comment is success');
      //console.log(response)
    } else {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
  } catch {
    // 피드백 전송 실패는 사용자 흐름에 영향 없음
  }
};

export const fetchStatusBarData = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/status`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json; charset=utf-8',
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    // console.error('Status Bar API Error:', error);
    return null;
  }
};
